import { parsePhoneNumber } from "awesome-phonenumber";
import moment from "moment";
import Validator from "validator";

import type { CreditCardBrand } from "@app/types/napi-types";

import formatter from "../utils/formatter";

export const VALIDATION_ERROR_NAMES = {
  CARD_EXPIRED: "card_expired",
  DOB_IN_FUTURE: "dob_in_future",
  EXPIRY_DATE_IN_PAST: "expiry_date_in_past",
  INVALID: "invalid",
  INVALID_COUNTRY_CODE3: "invalid_country_code3",
  INVALID_CREDIT_CARD: "invalid_credit_card",
  INVALID_NAME: "invalid_name",
  INVALID_DATE: "invalid_date",
  INVALID_DNI: "invalid_dni",
  INVALID_EMAIL: "invalid_email",
  INVALID_GEOHASH: "invalid_geohash",
  INVALID_PHONE: "invalid_phone_number",
  NOT_NUMERIC: "not_numeric",
  NOT_UNIQUE: "not_unique",
  PASSWORDS_DO_NOT_MATCH: "passwords_do_not_match",
  EMAILS_DO_NOT_MATCH: "emails_do_not_match",
  REQUIRED: "required",
  TOO_FEW_WORDS: "too_few_words",
  TOO_LONG: "too_long",
  TOO_SHORT: "too_short",
  UNSUPPORTED_CREDIT_CARD: "unsupported_credit_card",
  INVALID_TURKISH_ID: "invalid_turkish_id",
  FULL_NAME_NOT_UNIQUE: "full_name_not_unique"
} as const;

type ValidationErrorName =
  (typeof VALIDATION_ERROR_NAMES)[keyof typeof VALIDATION_ERROR_NAMES];

// A validator is a function that takes a string value and returns an error
// object or null. A validator creator function takes an optional message to
// attach to the error object, and returns a validor.

// Utilities
// ---------

export interface ValidationError {
  name: ValidationErrorName;
  message: string | null;
}

export type Validator = (
  value: string,
  comparison_value?: string
) => null | ValidationError;

/**
 * Create a validator out of an array of validators
 * Checks each one in order, returning the first error
 *
 * @param validators
 * @returns
 */
export function combineValidators(validators: Array<Validator>): Validator {
  return function (value, comparison_value) {
    let error: ValidationError | null = null;

    validators.forEach(validator => {
      if (!error) {
        error = validator(value, comparison_value);
      }
    });

    return error;
  };
}

/**
 * Only run the validator when the value is not empty
 * (use for optional inputs)
 *
 * @param validator
 * @returns
 */
export function validateWhenNotEmpty(validator: Validator): Validator {
  return function (value, comparison_value) {
    return typeof value === "number" ||
      value.length > 0 ||
      (comparison_value && comparison_value.length > 0)
      ? validator(value, comparison_value)
      : null;
  };
}

export function containNonAlphabeticCharacters(
  str: string,
  locale: string
): boolean {
  if (!(str.length > 0)) {
    return false;
  }
  const full_name = String(str).replace(/\s/g, "");
  return !isAlphabetic(full_name, locale);
}

// Validator creators
// ------------------

export function required(message: string | null = null): Validator {
  return function (value) {
    return (value && value.length > 0) || typeof value === "number"
      ? null
      : { name: VALIDATION_ERROR_NAMES.REQUIRED, message: message };
  };
}

export function numeric(message: string | null = null): Validator {
  return function (value) {
    return Validator.isNumeric(value)
      ? null
      : { name: VALIDATION_ERROR_NAMES.NOT_NUMERIC, message: message };
  };
}

export function minLength(
  min_length: number,
  message: string | null = null
): Validator {
  return function (value) {
    return Validator.isLength(value, {
      min: min_length
    })
      ? null
      : { name: VALIDATION_ERROR_NAMES.TOO_SHORT, message: message };
  };
}

export function maxLength(
  max_length: number,
  message: string | null = null
): Validator {
  return function (value) {
    return value.length <= max_length
      ? null
      : { name: VALIDATION_ERROR_NAMES.TOO_LONG, message: message };
  };
}

export function minWords(
  min_words: number,
  message: string | null = null
): Validator {
  return function (value) {
    let words = value.split(" ");
    words = words.filter(w => w.length !== 0);

    return words.length >= min_words
      ? null
      : { name: VALIDATION_ERROR_NAMES.TOO_FEW_WORDS, message: message };
  };
}

export function emailAddress(message: string | null = null): Validator {
  return function (value) {
    return Validator.isEmail(value)
      ? null
      : { name: VALIDATION_ERROR_NAMES.INVALID_EMAIL, message: message };
  };
}

export function isPhoneNumberValid(value: string): boolean {
  value = value.replace(/[()+\-\s]/g, "");
  return Validator.isNumeric(value);
}

export function phoneNumber(message: string | null = null): Validator {
  return function (value) {
    return isPhoneNumberValid(value)
      ? null
      : { name: VALIDATION_ERROR_NAMES.INVALID_PHONE, message: message };
  };
}

export function phoneNumberE164(message: string | null = null): Validator {
  return function (value) {
    return !value || parsePhoneNumber(value).valid
      ? null
      : { name: VALIDATION_ERROR_NAMES.INVALID_PHONE, message: message };
  };
}

export function passwordsMatch(message: string | null = null): Validator {
  return function (password, password_to_check_against) {
    const passwords_match = Validator.equals(
      password,
      password_to_check_against || ""
    );
    return passwords_match
      ? null
      : {
          name: VALIDATION_ERROR_NAMES.PASSWORDS_DO_NOT_MATCH,
          message: message
        };
  };
}

export function validName(message: string | null = null): Validator {
  return function (value) {
    return Validator.matches(value, /^[^_!¡?÷?¿/\\+=@#%¤$¥€£×*{}|<>;:[\]]*$/gi)
      ? null
      : { name: VALIDATION_ERROR_NAMES.INVALID_NAME, message: message };
  };
}

// This is really arbitrary, just to help users avoid typos like
// entering the year "0919" instead of "1990" for their date of birth
const MAX_AGE = 150;

export function dateOfBirth(
  message: string | null = null,
  today_timestamp?: string | undefined
): Validator {
  return function (value) {
    const formatted_value = formatter.passengerFormDateToDate(value);

    if (formatted_value instanceof Error) {
      return { name: VALIDATION_ERROR_NAMES.INVALID_DATE, message: message };
    } else {
      value = formatted_value;
    }

    const value_m = moment(value, "YYYY-MM-DD", true);
    if (!value_m.isValid()) {
      return { name: VALIDATION_ERROR_NAMES.INVALID_DATE, message: message };
    }

    const today_m = today_timestamp ? moment(today_timestamp) : moment();
    if (today_m.isBefore(value_m)) {
      return { name: VALIDATION_ERROR_NAMES.DOB_IN_FUTURE, message: message };
    }

    if (today_m.diff(value_m, "years") > MAX_AGE) {
      return { name: VALIDATION_ERROR_NAMES.INVALID_DATE, message: message };
    }

    return null;
  };
}

export function idExpiryDate(
  message: string | null = null,
  today_timestamp?: string | undefined
): Validator {
  return function (value) {
    const formatted_value = formatter.passengerFormDateToDate(value);

    if (formatted_value instanceof Error) {
      return { name: VALIDATION_ERROR_NAMES.INVALID_DATE, message: message };
    } else {
      value = formatted_value;
    }

    const value_m = moment(value, "YYYY-MM-DD", true);
    if (!value_m.isValid()) {
      return { name: VALIDATION_ERROR_NAMES.INVALID_DATE, message: message };
    }

    const today_m = today_timestamp ? moment(today_timestamp) : moment();
    if (today_m.isAfter(value_m)) {
      return {
        name: VALIDATION_ERROR_NAMES.EXPIRY_DATE_IN_PAST,
        message: message
      };
    }

    return null;
  };
}

export function countryCode3(message: string | null = null): Validator {
  return function (value) {
    // iso3 codes are 3 characters
    return value.length === 3
      ? null
      : {
          name: VALIDATION_ERROR_NAMES.INVALID_COUNTRY_CODE3,
          message: message
        };
  };
}

export function creditCard(message: string | null = null): Validator {
  return function (value) {
    value = value.replace(/ /g, "");

    return Validator.isCreditCard(value)
      ? null
      : { name: VALIDATION_ERROR_NAMES.INVALID_CREDIT_CARD, message: message };
  };
}

export function supportedCreditCard(
  supported_credit_cards: ReadonlyArray<CreditCardBrand>,
  message: string | null = null
): Validator {
  return function (value) {
    value = value.replace(/ /g, "");

    const card = formatter.getCardFromNumber(value);
    return card && supported_credit_cards.indexOf(card.type) > -1
      ? null
      : {
          name: VALIDATION_ERROR_NAMES.UNSUPPORTED_CREDIT_CARD,
          message: message
        };
  };
}

export function cardExpiry(
  message: string | null = null,
  today_timestamp?: string | undefined
): Validator {
  return function (value) {
    const formatted_value = formatter.expiryToDate(value);
    if (formatted_value instanceof Error) {
      return { name: VALIDATION_ERROR_NAMES.INVALID_DATE, message: message };
    } else {
      value = formatted_value;
    }

    const expiry_m = moment(value, "YYYY-MM-DD", true);
    if (!expiry_m.isValid()) {
      return { name: VALIDATION_ERROR_NAMES.INVALID_DATE, message: message };
    }

    const today_m = today_timestamp ? moment(today_timestamp) : moment();

    if (expiry_m.diff(today_m, "months") < 0) {
      return { name: VALIDATION_ERROR_NAMES.CARD_EXPIRED, message: message };
    }

    return null;
  };
}

export function geohash(
  length: number,
  message: string | null = null
): Validator {
  return function (geohash) {
    if (!Validator.isAlphanumeric(geohash) || geohash.length !== length) {
      return { name: VALIDATION_ERROR_NAMES.INVALID_GEOHASH, message: message };
    }

    return null;
  };
}

export function isAlphabetic(value: string, locale: string): boolean {
  // The validation doesn't test Chinese characters, so we return true to prevent showing warnings to users.
  if (locale === "zh") {
    return true;
  }
  /**
   * This regex should provide support for a range of Cyrillic characters.
   * In addition, the regex provides support for a couple of symbols, '.' and '-' (e.g. Jean-Marc).
   */
  if (locale === "ru") {
    return /^[а-я-ёА-Я-Ё\.\s]+$/.test(value);
  }

  /**
   * This regex, [a-zA-Z\u00C0-\u024F], should provide support for a range of accented Latin characters.
   * For more info on accented characters, see this link: https://stackoverflow.com/questions/20690499/concrete-javascript-regex-for-accented-characters-diacritics
   * In addition, the regex provides support for a couple of symbols, '.' and '-' (e.g. Jean-Marc).
   */
  return /^[a-zA-Z\u00C0-\u024F-\.\s]+$/.test(value);
}

/**
 * @see https://gist.github.com/onury/7a380f906b1eb46dc2f0bb089caf7d12
 *
 * @param message
 * @returns
 */
export function turskishId(message: string | null = null): Validator {
  const validation_error = {
    name: VALIDATION_ERROR_NAMES.INVALID_TURKISH_ID,
    message
  };

  return id_number => {
    // T.C. identity number should have 11 digits and first should be positive.
    const regexp = new RegExp(/^[1-9]\d{10}$/);
    if (!regexp.test(id_number)) {
      return validation_error;
    }

    let sum_ten_first = 0;
    let sum_even_pos = 0;
    let sum_odd_pos = 0;

    id_number.split("").forEach((digit, idx) => {
      const digit_int = parseInt(digit, 10);

      if (idx < 10) {
        sum_ten_first += digit_int;
      }

      if (idx < 9) {
        if ((idx + 1) % 2 === 0) {
          sum_even_pos += digit_int;
        } else {
          sum_odd_pos += digit_int;
        }
      }
    });

    const tenth_digit = parseInt(id_number.charAt(9), 10);
    const eleventh_digit = parseInt(id_number.charAt(10), 10);

    // check if the unit-digit of the sum of first 10 digits equals to the 11th digit.
    if (sum_ten_first % 10 !== eleventh_digit) {
      return validation_error;
    }

    // check if unit-digit of the sum of odds * 7 + evens * 9 is equal to 10th digit.
    if ((sum_odd_pos * 7 + sum_even_pos * 9) % 10 !== tenth_digit) {
      return validation_error;
    }

    // check if unit-digit of the sum of odds * 8 is equal to 11th digit.
    if ((sum_odd_pos * 8) % 10 !== eleventh_digit) {
      return validation_error;
    }

    return null;
  };
}

export function emailsMatch(message: string | null = null): Validator {
  return function (email, email_to_check_against) {
    const emails_match = Validator.equals(email, email_to_check_against || "");
    return emails_match
      ? null
      : {
          name: VALIDATION_ERROR_NAMES.EMAILS_DO_NOT_MATCH,
          message: message
        };
  };
}

export function regExp(
  reg_exp: RegExp,
  message: string | null = null
): Validator {
  return function (value) {
    if (!value.match(reg_exp)) {
      return { name: VALIDATION_ERROR_NAMES.INVALID, message: message };
    }

    return null;
  };
}

export const firstName = combineValidators([
  required(),
  minLength(2),
  maxLength(40),
  validName()
]);

export const lastName = combineValidators([
  required(),
  minLength(2),
  maxLength(40),
  validName()
]);

export const email = combineValidators([required(), emailAddress()]);

export const signinPassword = combineValidators([required(), minLength(3)]);

export const signupPassword = combineValidators([
  required(),
  minLength(8),
  maxLength(32)
]);

export const confirmPassword = combineValidators([
  signupPassword,
  passwordsMatch()
]);

export const confirmEmail = combineValidators([email, emailsMatch()]);
